import {
  Box,
  Button,
  ColumnLayout,
  Container,
  Header,
  Link,
  StatusIndicator,
} from "@amzn/awsui-components-react";
import {
  DLQRedriveInfoResultMetadata,
  Stage,
} from "../../lusca-search-api/generated-src";
import {
  generateExecutionUrlFromStepFunction,
  generateMcmUrl,
  generateS3ObjectUrl,
} from "../../helpers/logUtils";

interface Props {
  status?: string;
  metadata?: DLQRedriveInfoResultMetadata;
  executionArn: string;
  stage: Stage;
  loading: boolean;
}

function ValueWithLabel({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Box variant="awsui-key-label">{label}</Box>
      <div>{children}</div>
    </div>
  );
}

const statusType = (status?: string) =>
  status === "COMPLETED"
    ? "success"
    : status === "PENDING"
    ? "in-progress"
    : "error";

// The source of message ids: the uploaded file's S3 URI (rendered as a console
// link) or an inline count.
function Source({
  metadata,
  stage,
}: {
  metadata?: DLQRedriveInfoResultMetadata;
  stage: Stage;
}) {
  if (metadata?.messageIdsS3Uri) {
    const s3Url = generateS3ObjectUrl(stage, metadata.messageIdsS3Uri);
    return s3Url ? (
      <Link href={s3Url} external externalIconAriaLabel="Opens in a new tab">
        {metadata.messageIdsS3Uri}
      </Link>
    ) : (
      <>{metadata.messageIdsS3Uri}</>
    );
  }
  if (metadata?.messageCount != null) {
    return <>{`${metadata.messageCount} message id(s)`}</>;
  }
  return <>-</>;
}

export function DlqRedriveExecutionDetails({
  status,
  metadata,
  executionArn,
  stage,
  loading,
}: Props) {
  const fallback = loading ? "Loading…" : "unknown";

  return (
    <Container
      header={
        <Header
          variant="h2"
          actions={
            <Button
              iconName="external"
              iconAlign="right"
              onClick={() =>
                window.open(
                  generateExecutionUrlFromStepFunction(stage, executionArn),
                  "_blank"
                )
              }
            >
              View in Console
            </Button>
          }
        >
          Redrive Execution
        </Header>
      }
    >
      <ColumnLayout columns={3} variant="text-grid">
        <ValueWithLabel label="Stage">{stage}</ValueWithLabel>
        <ValueWithLabel label="Status">
          {status ? (
            <StatusIndicator type={statusType(status)}>
              {status === "PENDING" ? "In progress…" : status}
            </StatusIndicator>
          ) : (
            fallback
          )}
        </ValueWithLabel>
        <ValueWithLabel label="Executor">
          {metadata?.executor || fallback}
        </ValueWithLabel>
        <ValueWithLabel label="Request Date">
          {metadata?.timestamp
            ? new Date(metadata.timestamp * 1000).toLocaleString()
            : loading
            ? "Loading…"
            : "-"}
        </ValueWithLabel>
        <ValueWithLabel label="Type">
          {metadata?.requestType || fallback}
        </ValueWithLabel>
        <ValueWithLabel label="Source">
          <Source metadata={metadata} stage={stage} />
        </ValueWithLabel>
        {metadata?.deleteWithoutRedriving && (
          <ValueWithLabel label="MCM">
            {metadata.mcmId ? (
              <Link
                href={generateMcmUrl(stage, metadata.mcmId)}
                external
                externalIconAriaLabel="Opens in a new tab"
              >
                {metadata.mcmId}
              </Link>
            ) : (
              "-"
            )}
          </ValueWithLabel>
        )}
      </ColumnLayout>
    </Container>
  );
}
